<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen py-10 px-4">
    <AppHeader />

    <div class="max-w-5xl mx-auto space-y-6">
      <!-- HEADER -->
      <div class="bg-white shadow rounded-2xl p-6 flex flex-col gap-4">
        <div class="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 class="text-3xl font-bold">Disputa</h1>
            <p class="text-gray-500 text-sm mt-1" v-if="dispute?.created_at">
              Aberta em {{ formatDate(dispute.created_at) }}
            </p>
          </div>

          <div class="flex flex-col items-start gap-2">
            <span class="px-3 py-1 text-xs font-semibold rounded-full" :class="statusPillClass">
              {{ statusLabel }}
            </span>

            <div v-if="dispute?.total_amount" class="text-sm text-gray-600">
              Total em disputa:
              <span class="font-semibold text-gray-900">
                {{ money(dispute.total_amount) }}
              </span>
            </div>
          </div>
        </div>

        <!-- INFO PRINCIPAL -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <div class="bg-gray-50 p-4 rounded-xl">
            <p class="text-sm text-gray-500">Projeto</p>
            <p class="text-lg font-semibold">
              {{ dispute?.project?.title || "-" }}
            </p>
            <p class="text-xs text-gray-500 mt-1" v-if="dispute?.project?.hash && dispute?.project?.slug">
              #{{ dispute.project.hash }} / {{ dispute.project.slug }}
            </p>
          </div>

          <div class="bg-gray-50 p-4 rounded-xl">
            <p class="text-sm text-gray-500">Contratante</p>
            <p class="text-lg font-semibold">
              {{ dispute?.contractor?.name || "-" }}
            </p>
          </div>

          <div class="bg-gray-50 p-4 rounded-xl">
            <p class="text-sm text-gray-500">Freelancer</p>
            <p class="text-lg font-semibold">
              {{ dispute?.freelancer?.name || "-" }}
            </p>
          </div>
        </div>
      </div>

      <!-- ALERTAS -->
      <div
        v-if="dispute?.status === 'agreed'"
        class="bg-green-50 border border-green-200 text-green-800 rounded-2xl p-4"
      >
        Valores enviados. A plataforma irá analisar e definir a divisão final.
      </div>

      <div
        v-else-if="dispute?.status === 'resolved'"
        class="bg-blue-50 border border-blue-200 text-blue-800 rounded-2xl p-4"
      >
        Disputa finalizada.
      </div>

      <div v-else class="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-4">
        <p class="font-semibold">Disputa em análise</p>
        <p class="mt-1 text-sm">
          Contratante e freelancer devem informar os valores sugeridos. A plataforma irá
          analisar e definir a divisão final. Se ninguém enviar em até
          <strong>10 dias úteis</strong>, a plataforma decidirá com base no histórico do projeto.
        </p>
      </div>

      <!-- INSTRUÇÕES -->
      <div v-if="dispute?.status === 'open' || dispute?.status === 'pending'" class="bg-white shadow rounded-2xl p-6">
        <h2 class="text-lg font-semibold">Como funciona</h2>
        <ul class="mt-3 text-sm text-gray-700 space-y-2 list-disc ml-5">
          <li>O contratante informa quanto acredita que deve receber de reembolso.</li>
          <li>O freelancer informa quanto acredita que deve receber de repasse.</li>
          <li>A plataforma analisa e define a divisão final.</li>
        </ul>
      </div>

      <!-- ENVIAR VALORES -->
      <div class="bg-white shadow rounded-2xl p-6">
        <div class="flex justify-between items-center flex-wrap gap-3">
          <h2 class="text-xl font-semibold">Informar valores sugeridos</h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label class="font-medium">Sugestão do contratante (reembolso)</label>
            <input
              v-model="offer.contractor_amount_formatted"
              @input="onInputMoney('contractor')"
              type="text"
              class="input-gold"
              placeholder="R$ 0,00"
              :disabled="!canCreateOffer"
            />
          </div>

          <div>
            <label class="font-medium">Sugestão do freelancer (repasse)</label>
            <input
              v-model="offer.freelancer_amount_formatted"
              @input="onInputMoney('freelancer')"
              type="text"
              class="input-gold"
              placeholder="R$ 0,00"
              :disabled="!canCreateOffer"
            />
          </div>

          <div class="md:col-span-2">
            <div class="bg-gray-50 rounded-xl p-4 flex flex-col gap-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Total em disputa</span>
                <span class="font-semibold">
                  {{ dispute?.total_amount ? money(dispute.total_amount) : "-" }}
                </span>
              </div>
            </div>
          </div>

          <div class="md:col-span-2">
  <label class="font-medium">Justificativa (opcional)</label>
  <textarea
    v-model="offer.note"
    class="input-gold min-h-[120px] resize-y"
    :maxlength="NOTE_MAX"
    placeholder="Explique o motivo dos valores sugeridos e/ou relate o que aconteceu no cancelamento/disputa..."
    :disabled="!canCreateOffer"
  ></textarea>

  <div class="flex justify-between mt-1 text-xs text-gray-500">
    <span>Quanto mais detalhes, melhor para a análise da plataforma.</span>
    <span>{{ (offer.note || '').length }}/{{ NOTE_MAX }}</span>
  </div>
</div>

          <div class="md:col-span-2 flex justify-end">
            <button
              @click="submitSuggestedValues"
              :disabled="loadingAction || !canSubmit"
              class="bg-[#b99a52] hover:bg-[#a18446] text-white px-6 py-3 rounded-xl font-semibold shadow disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span v-if="!loadingAction">Enviar valores</span>
              <span v-else>Enviando...</span>
            </button>
          </div>
        </div>
      </div>

      <AppFooter />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import api from "@/services/api";
import { useLoadingStore } from "@/store/loadingStore";

const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();

const loadingAction = ref(false);
const dispute = ref(null);

const NOTE_MAX = 2000;

const offer = ref({
  note: "",
  contractor_amount: 0,
  freelancer_amount: 0,
  contractor_amount_formatted: "",
  freelancer_amount_formatted: "",
});

const canCreateOffer = computed(() => {
  return !!dispute.value && ["open", "pending"].includes(dispute.value.status);
});

const totalDisputeAmount = computed(() => Number(dispute.value?.total_amount || 0));

const canSubmit = computed(() => {
  if (!canCreateOffer.value) return false;

  const total = totalDisputeAmount.value;
  const c = Number(offer.value.contractor_amount || 0);
  const f = Number(offer.value.freelancer_amount || 0);

  // precisa pelo menos 1 maior que zero
  if (c <= 0 && f <= 0) return false;

  // não aceita negativo
  if (c < 0 || f < 0) return false;

  // não aceita maior que o total
  if (c > total || f > total) return false;

  // soma não pode passar do total
  if (c + f > total) return false;

  return true;
});

const statusLabel = computed(() => {
  const s = dispute.value?.status;
  return s === "open" || s === "pending"
    ? "Aberta"
    : s === "agreed"
    ? "Valores enviados"
    : s === "resolved"
    ? "Finalizada"
    : "—";
});

const statusPillClass = computed(() => {
  const s = dispute.value?.status;
  if (s === "open" || s === "pending") return "bg-fuchsia-100 text-fuchsia-700";
  if (s === "agreed") return "bg-green-100 text-green-700";
  if (s === "resolved") return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-gray-700";
});

function formatDate(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleDateString("pt-BR");
}

function money(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

function parseMoneyInput(value) {
  const clean = String(value || "").replace(/\D/g, "");
  const num = parseInt(clean, 10) / 100;
  return isNaN(num) ? 0 : num;
}

function formatMoneyBRL(num) {
  const n = Number(num || 0);
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

function clamp(val, min, max) {
  const n = Number(val || 0);
  if (Number.isNaN(n)) return 0;
  return Math.max(min, Math.min(max, n));
}

/**
 * Regra:
 * - não pode ser < 0
 * - não pode ser > total
 * - e não pode somar (c+f) > total
 * Então: ao digitar um lado, clamp no máximo (total - outro_lado).
 */
function onInputMoney(which) {
  const total = totalDisputeAmount.value;

  if (which === "contractor") {
    let num = parseMoneyInput(offer.value.contractor_amount_formatted);

    const other = Number(offer.value.freelancer_amount || 0);
    const maxAllowed = Math.max(0, total - other);

    num = clamp(num, 0, maxAllowed);

    offer.value.contractor_amount = num;
    offer.value.contractor_amount_formatted = num > 0 ? formatMoneyBRL(num) : "";
    return;
  }

  if (which === "freelancer") {
    let num = parseMoneyInput(offer.value.freelancer_amount_formatted);

    const other = Number(offer.value.contractor_amount || 0);
    const maxAllowed = Math.max(0, total - other);

    num = clamp(num, 0, maxAllowed);

    offer.value.freelancer_amount = num;
    offer.value.freelancer_amount_formatted = num > 0 ? formatMoneyBRL(num) : "";
    return;
  }
}

async function fetchDispute() {
  try {
    loadingStore.show();
    const id = route.params.id;
    const { data } = await api.get(`/disputes/${id}`);
    dispute.value = data.data?.dispute || data.data || null;
  } catch (e) {
    proxy?.$swal?.fire({
      icon: "error",
      title: "",
      text: e?.response?.data?.error || "Erro ao carregar disputa.",
    });
  } finally {
    loadingStore.hide();
  }
}

function validateBeforeSubmit() {
  const total = totalDisputeAmount.value;
  const c = Number(offer.value.contractor_amount || 0);
  const f = Number(offer.value.freelancer_amount || 0);

  const note = String(offer.value.note || "").trim();
  if (note.length > NOTE_MAX) return `A justificativa deve ter no máximo ${NOTE_MAX} caracteres.`;

  if (c < 0 || f < 0) return "Valores não podem ser negativos.";
  if (c <= 0 && f <= 0) return "Informe ao menos um valor maior que zero.";
  if (c > total || f > total) return "Nenhum valor pode ser maior que o total em disputa.";
  if (c + f > total) return "A soma das sugestões não pode ultrapassar o total em disputa.";

  return null;
}

async function submitSuggestedValues() {
  try {
    const err = validateBeforeSubmit();
    if (err) {
      await proxy?.$swal?.fire({ icon: "warning", title: "", text: err });
      return;
    }

    loadingAction.value = true;
    loadingStore.show();

    const id = route.params.id;

    const payload = {
      contractor_amount: Number(offer.value.contractor_amount || 0),
      freelancer_amount: Number(offer.value.freelancer_amount || 0),
      note: String(offer.value.note || "").trim(),
    };

    await api.post(`/disputes/${id}/offers`, payload);

    await proxy?.$swal?.fire({
      icon: "success",
      title: "",
      text: "Valores enviados com sucesso!",
    });

    // redirect para /projeto/:hash/:slug
    const hash = dispute.value?.project?.hash;
    const slug = dispute.value?.project?.slug;

    if (hash && slug) {
      router.push(`/projeto/${hash}/${slug}`);
      return;
    }

    // fallback
    router.back();
  } catch (e) {
    proxy?.$swal?.fire({
      icon: "error",
      title: "",
      text: e?.response?.data?.error || "Erro ao enviar valores.",
    });
  } finally {
    loadingStore.hide();
    loadingAction.value = false;
  }
}

onMounted(fetchDispute);
</script>

<style>
.swal-top-layer {
  z-index: 99999 !important;
}
</style>